/**
 * /admin/beta — admin-only invite management.
 * - Stats banner (pending / redeemed / expired / revoked)
 * - Generate invites form (count + label + expiry days)
 * - Invite table (token / label / status / created / expires / redeemed_by + copy/revoke actions)
 */
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
    Plus, Copy, Trash2, Loader2, RefreshCw, Rocket, Search,
    CheckCircle2, Clock3, XCircle, Ban,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { getJSON, postJSON, delJSON } from "../../lib/api";
import { usePageMeta } from "../../lib/usePageMeta";

const STATUS_META = {
    pending:  { label: "Pending",  color: "bg-accent-cyan/15 text-accent-cyan border-accent-cyan/40",  icon: Clock3 },
    redeemed: { label: "Redeemed", color: "bg-accent-mint/15 text-accent-mint border-accent-mint/40", icon: CheckCircle2 },
    expired:  { label: "Expired",  color: "bg-accent-amber/15 text-accent-amber border-accent-amber/40", icon: XCircle },
    revoked:  { label: "Revoked",  color: "bg-accent-red/15 text-accent-red border-accent-red/40",     icon: Ban },
};

const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export const AdminBeta = () => {
    usePageMeta({ title: "Beta invites — Admin · Cybersecurity Forefront", description: "Manage beta tester invites." });

    const [data, setData] = useState({ items: [], stats: {} });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [filter, setFilter] = useState("");
    const [form, setForm] = useState({ count: 5, label: "", expires_in_days: 30 });

    const load = async () => {
        setLoading(true);
        try {
            const res = await getJSON("/admin/beta/invites");
            setData(res);
        } catch (e) {
            toast.error(e?.message || "Failed to load invites");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const generate = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const res = await postJSON("/admin/beta/invites", {
                count: Number(form.count),
                label: form.label.trim() || null,
                expires_in_days: Number(form.expires_in_days),
            });
            toast.success(`Generated ${res.created} invite(s)`);
            await load();
        } catch (e) {
            toast.error(e?.message || "Could not generate invites");
        } finally {
            setBusy(false);
        }
    };

    const revoke = async (token) => {
        if (!window.confirm(`Revoke invite ${token}?`)) return;
        try {
            await delJSON(`/admin/beta/invites/${encodeURIComponent(token)}`);
            toast.success("Revoked");
            await load();
        } catch (e) {
            toast.error(e?.message || "Could not revoke");
        }
    };

    const copyLink = (token) => {
        const url = `${window.location.origin}/beta/redeem/${token}`;
        navigator.clipboard.writeText(url).then(
            () => toast.success("Link copied"),
            () => toast.error("Copy failed"),
        );
    };

    const filtered = useMemo(() => {
        const q = filter.toLowerCase().trim();
        if (!q) return data.items;
        return data.items.filter((it) =>
            (it.token || "").toLowerCase().includes(q) ||
            (it.label || "").toLowerCase().includes(q) ||
            (it.redeemed_by_email || "").toLowerCase().includes(q),
        );
    }, [data.items, filter]);

    return (
        <div className="min-h-screen bg-canvas text-text-primary" data-testid="admin-beta-page">
            <AdminHeader />
            <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                    <div>
                        <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">Admin</p>
                        <h1 className="mt-1 font-display font-semibold text-3xl tracking-tight">Beta invites</h1>
                    </div>
                    <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/[0.08] text-[12.5px] text-text-muted hover:text-accent-cyan hover:border-accent-cyan/30">
                        <RefreshCw className="w-3.5 h-3.5" /> Refresh
                    </button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-7">
                    <StatTile label="Pending" value={data.stats.pending} icon={Clock3} tone="cyan" />
                    <StatTile label="Redeemed" value={data.stats.redeemed} icon={CheckCircle2} tone="mint" />
                    <StatTile label="Expired" value={data.stats.expired} icon={XCircle} tone="amber" />
                    <StatTile label="Revoked" value={data.stats.revoked} icon={Ban} tone="red" />
                </div>

                {/* Generate form */}
                <section className="mb-7 rounded-xl border border-white/[0.06] bg-surface/60 p-5" data-testid="generate-invites-form">
                    <div className="flex items-center gap-2 mb-3">
                        <Plus className="w-4 h-4 text-accent-cyan" />
                        <h2 className="font-display font-semibold text-[14.5px]">Generate new invites</h2>
                    </div>
                    <form onSubmit={generate} className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                        <NumberField label="Count (1-20)" value={form.count} min={1} max={20}
                            onChange={(v) => setForm({ ...form, count: v })} testId="invite-count" />
                        <TextField label="Label (optional)" value={form.label}
                            onChange={(v) => setForm({ ...form, label: v })} testId="invite-label" />
                        <NumberField label="Expires in (days)" value={form.expires_in_days} min={1} max={365}
                            onChange={(v) => setForm({ ...form, expires_in_days: v })} testId="invite-expiry" />
                        <button type="submit" disabled={busy} data-testid="generate-submit"
                            className="self-end inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md bg-accent-cyan text-canvas text-[12.5px] font-medium hover:shadow-glow-cyan disabled:opacity-50">
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
                            Generate
                        </button>
                    </form>
                </section>

                {/* Filter */}
                <div className="mb-3 flex items-center gap-2">
                    <Search className="w-3.5 h-3.5 text-text-dim" />
                    <input type="text" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by token, label, email…"
                        data-testid="invite-filter"
                        className="flex-1 px-2.5 py-1.5 bg-canvas border border-white/[0.08] rounded-md text-[12.5px] focus:outline-none focus:border-accent-cyan" />
                    <span className="text-[11.5px] text-text-dim font-mono">{filtered.length}/{data.items.length}</span>
                </div>

                {/* List */}
                {loading ? (
                    <div className="flex items-center gap-2 text-text-muted text-[12.5px] py-10 justify-center">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                ) : (
                    <div className="rounded-xl border border-white/[0.06] bg-surface/40 overflow-x-auto" data-testid="invite-list">
                        <table className="w-full text-[12.5px]">
                            <thead>
                                <tr className="text-left text-[10.5px] font-mono uppercase tracking-[0.18em] text-text-dim border-b border-white/[0.06]">
                                    <th className="px-4 py-3">Token</th>
                                    <th className="px-4 py-3">Label</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3">Created</th>
                                    <th className="px-4 py-3">Expires</th>
                                    <th className="px-4 py-3">Redeemed by</th>
                                    <th className="px-4 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((it) => {
                                    const meta = STATUS_META[it.status] || STATUS_META.pending;
                                    const Icon = meta.icon;
                                    return (
                                        <tr key={it.token} className="border-b border-white/[0.04]" data-testid={`invite-row-${it.token}`}>
                                            <td className="px-4 py-2.5 font-mono text-accent-cyan">{it.token}</td>
                                            <td className="px-4 py-2.5 text-text-muted truncate max-w-[16rem]">{it.label || "—"}</td>
                                            <td className="px-4 py-2.5">
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10.5px] font-mono border ${meta.color}`}>
                                                    <Icon className="w-3 h-3" /> {meta.label}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2.5 text-text-muted">{fmt(it.created_at)}</td>
                                            <td className="px-4 py-2.5 text-text-muted">{fmt(it.expires_at)}</td>
                                            <td className="px-4 py-2.5 text-text-muted">{it.redeemed_by_email || "—"}</td>
                                            <td className="px-4 py-2.5 text-right">
                                                <button onClick={() => copyLink(it.token)} data-testid={`copy-${it.token}`}
                                                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-white/[0.08] text-[11px] text-text-muted hover:text-accent-cyan hover:border-accent-cyan/30 mr-1">
                                                    <Copy className="w-3 h-3" /> Copy
                                                </button>
                                                {["pending", "expired"].includes(it.status) && (
                                                    <button onClick={() => revoke(it.token)} data-testid={`revoke-${it.token}`}
                                                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-white/[0.08] text-[11px] text-text-dim hover:text-accent-red hover:border-accent-red/30">
                                                        <Trash2 className="w-3 h-3" />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={7} className="px-4 py-10 text-center text-text-dim">No invites match.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>
        </div>
    );
};

const StatTile = ({ label, value, icon: Icon, tone }) => {
    const colors = {
        cyan: "border-accent-cyan/30 text-accent-cyan", mint: "border-accent-mint/30 text-accent-mint",
        amber: "border-accent-amber/30 text-accent-amber", red: "border-accent-red/30 text-accent-red",
    };
    return (
        <div className={`rounded-xl border bg-surface/40 p-4 ${colors[tone] || colors.cyan}`} data-testid={`stat-${label.toLowerCase()}`}>
            <div className="flex items-center gap-1.5 text-[10.5px] font-mono uppercase tracking-[0.18em]">
                <Icon className="w-3 h-3" /> {label}
            </div>
            <div className="mt-2 text-3xl font-display font-semibold text-text-primary tabular-nums">{value ?? 0}</div>
        </div>
    );
};

const NumberField = ({ label, value, onChange, testId, ...rest }) => (
    <label className="block">
        <span className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-text-muted">{label}</span>
        <input type="number" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
            className="mt-1 w-full px-3 py-2 bg-canvas border border-white/[0.08] rounded-md text-[13px] focus:outline-none focus:border-accent-cyan"
            {...rest} />
    </label>
);
const TextField = ({ label, value, onChange, testId }) => (
    <label className="block">
        <span className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-text-muted">{label}</span>
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
            className="mt-1 w-full px-3 py-2 bg-canvas border border-white/[0.08] rounded-md text-[13px] focus:outline-none focus:border-accent-cyan" />
    </label>
);

export const AdminHeader = () => (
    <header className="border-b border-white/[0.06] bg-canvas/85 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-3 flex items-center gap-4">
            <Link to="/dashboard" className="text-accent-cyan font-display font-semibold text-[14px]">← Dashboard</Link>
            <nav className="flex gap-3 text-[12.5px]">
                <Link to="/admin/beta" className="text-text-primary hover:text-accent-cyan">Beta invites</Link>
                <Link to="/admin/feedback" className="text-text-muted hover:text-accent-cyan">Feedback</Link>
            </nav>
        </div>
    </header>
);

export default AdminBeta;
